import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { findSearchProfileGroups, findSearchUsers, findSecComponents, findSecGroupComponents } from '../Security/SecFunctions';
import SaveIcon from '@material-ui/icons/Save';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Box, Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Switch from '@material-ui/core/Switch';
import { handleError } from 'reducers/ErrorReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { getService } from 'reducers/service';
import Content from '../Content';
import { notify } from 'reducers/NotifierReducer';
import { showAddSecUser } from 'reducers/DialogsReducer';
import Dialog from 'components/Dialog';
import { XGrid } from '@material-ui/x-grid';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '20px',
  },
  tableWrapper: {
    marginTop: '20px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: theme.shadows[2],
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
    padding: '16px',
    background: theme.palette.grey[200],
    fontWeight: 600,
    textAlign: 'center',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
    padding: '16px',
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  centerCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  moduleCell: {
    display: 'flex',
    justifyContent: 'center',
    fontWeight: 500,
  },
  badge: {
    minWidth: '60px',
    padding: '6px 14px',
    borderRadius: '20px',
    fontWeight: 600,
    textAlign: 'center',
  },
  progressWrapper: {
    width: '80%',
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  saveBtn: {
    textAlign: 'right',
    marginBottom: '10px',
  },
  dialogGrid: {
    height: 400,
    width: '100%',
  },
  excludedUsersSection: {
    marginTop: '30px',
    padding: '20px',
  },
  excludedUsersGrid: {
    height: 350,
    width: '100%',
    marginTop: '16px',
  },
  addUserRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  // Access control switch styles
  accessEnabled: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#2e7d32',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#2e7d32',
    },
  },
  accessDisabled: {
    '& .MuiSwitch-switchBase': {
      color: '#d32f2f',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#d32f2f',
    },
  },
  accessLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  accessEnabledText: {
    color: '#2e7d32',
  },
  accessDisabledText: {
    color: '#d32f2f',
  },
}));

export const sortPPlusModules = data => {
  const mappedValues = { cad: 0, mobile: 1, rms: 2, settings: 3, 'incident archive': 4, analytics: 5, ams: 6 };
  const newArr = new Array(data.length).fill(null);
  data.forEach(item => {
    const position = mappedValues[item.Label.toLowerCase()];
    if (position !== undefined) {
      newArr[position] = item;
    }
  });
  return newArr.filter(Boolean);
};

function LicenseManagement(props) {
  const classes = useStyles();
  const [modules, setModules] = useState([]);
  const [maxUsers, setMaxUsers] = useState({});
  const [assignedUsers, setAssignedUsers] = useState({});
  const [licenseEditMode, setLicenseEditMode] = useState({});
  const [moduleAccess, setModuleAccess] = useState({});
  const [accessEditMode, setAccessEditMode] = useState({});
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [profileGroupsData, setProfileGroupsData] = useState([]);
  const [groupComponentsData, setGroupComponentsData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [excludedUsers, setExcludedUsers] = useState([]);
  const [selectedUserToExclude, setSelectedUserToExclude] = useState(null);
  const cdrService = getService('cdr');
  const usersService = getService('settings-security-search-users');
  const profileGroupService = getService('settings-sec-profile-groups');
  const groupComponentService = getService('settings-sec-group-components');
  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    props.showSpinner();
    try {
      const allComponents = await findSecComponents();
      const filtered = (allComponents || []).filter(
        c => c.Type === 'Modules' && c.Flags?.trim() === 'P+'
      );
  
      const sorted = sortPPlusModules(filtered);
      setModules(sorted);
      await calculateUsedLicenses(sorted);
      const newMaxUsers = {};
      const newEditMode = {};
      const newModuleAccess = {};
      const newAccessEditMode = {};
      for (const module of sorted) {
        const licensePath = `Settings.License.${module.Label}`;
        const accessPath = `Settings.Application.${module.Label}`;

        const licenseData = await cdrService.get({ Path: licensePath, CDRKey: 'LicenseLimit' });
        const licenseItem = licenseData?.[0];
        if (licenseItem) {
          newEditMode[module.ptsComponentID] = true;
          newMaxUsers[module.ptsComponentID] = licenseItem.ValueNumeric ?? 0;
        } else {
          newEditMode[module.ptsComponentID] = false;
          newMaxUsers[module.ptsComponentID] = 0;
        }

        const accessData = await cdrService.get({ Path: accessPath, CDRKey: 'AccessControl' });
        const accessItem = accessData?.[0];
        if (accessItem) {
          newAccessEditMode[module.ptsComponentID] = true;
          newModuleAccess[module.ptsComponentID] = accessItem.ValueBoolean == null ? true : Boolean(accessItem.ValueBoolean);
        } else {
          newAccessEditMode[module.ptsComponentID] = false;
          newModuleAccess[module.ptsComponentID] = true;
        }
      }
      setMaxUsers(newMaxUsers);
      setLicenseEditMode(newEditMode);
      setModuleAccess(newModuleAccess);
      setAccessEditMode(newAccessEditMode);
    } catch (err) {
      props.handleError(err, 'Error fetching modules');
    } finally {
      props.hideSpinner();
    }
  };

  const calculateUsedLicenses = async (moduleList) => {
    try {
      const [usersRes, profileGroupsRes, groupComponentsRes] =
        await Promise.all([
          findSearchUsers(),
          findSearchProfileGroups(),
          findSecGroupComponents(),
        ]);
  
      const users = usersRes.data || usersRes;
      const profileGroups = profileGroupsRes.data || profileGroupsRes;
      const groupComponents = groupComponentsRes.data || groupComponentsRes;

      setUsersData(users);
      setProfileGroupsData(profileGroups);
      setGroupComponentsData(groupComponents);
      
      // Fetch excluded user IDs once and use for both state and filtering
      let excludedIds = [];
      try {
        const excludedData = await cdrService.get({ Path: 'Settings.License.ExcludedUsers', CDRKey: 'UserIds' });
        if (excludedData?.[0]?.ValueJson) {
          excludedIds = JSON.parse(excludedData[0].ValueJson).map(id => parseInt(id)).filter(id => !isNaN(id));
          const excludedUsersList = users.filter(u => excludedIds.includes(u.ptsUserID));
          setExcludedUsers(excludedUsersList);
        } else {
          setExcludedUsers([]);
        }
      } catch (e) {
        console.error('Error fetching excluded users:', e);
        setExcludedUsers([]);
      }

      // Only P+ Modules
      const validModules = moduleList.filter(
        (m) =>
          m.Type === "Modules" &&
          m.Flags?.trim() === "P+"
      );
  
      const result = {};
  
      validModules.forEach((module) => {
        const componentID = Number(module.ptsComponentID);
  
        const groupIDs = groupComponents
          .filter(
            (gc) =>
              Number(gc.ptsComponentID) === componentID &&
              Number(gc.IsDeleted) === 0 &&
              Number(gc.Permissions) === 1
          )
          .map((gc) => Number(gc.ptsGroupID));
          
          if (!groupIDs.length) {
            result[componentID] = 0;
            return;
          }
          
          const profileIDs = profileGroups
            .filter(pg => Number(pg.isDeleted) === 0 && groupIDs.includes(Number(pg.ptsGroupID)))
            .map(pg => Number(pg.ptsProfileID));
          
        if (!profileIDs.length) {
          result[componentID] = 0;
          return;
        }

        const userSet = new Set();  
        users.forEach((user) => {
          if (
            Number(user.IsDeleted) === 0 &&
            Number(user.IsDisabled) === 0 &&
            profileIDs.includes(Number(user.ptsProfileID)) &&
            !excludedIds.includes(user.ptsUserID)
          ) {
            userSet.add(user.ptsUserID);
          }
        });
        result[componentID] = userSet.size;
      });
      setAssignedUsers(result);
    } catch (err) {
      props.handleError(err, "Error calculating used licenses");
    }
  };

  const handleMaxUserChange = (id, value) => {
    setMaxUsers(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const fetchExcludedUsers = async (users) => {
    try {
      const data = await cdrService.get({ Path: 'Settings.License.ExcludedUsers', CDRKey: 'UserIds' });
      if (data?.[0]?.ValueJson) {
        const ids = JSON.parse(data[0].ValueJson).map(id => parseInt(id)).filter(id => !isNaN(id));
        const excludedUsersList = users.filter(u => ids.includes(u.ptsUserID));
        setExcludedUsers(excludedUsersList);
      } else {
        setExcludedUsers([]);
      }
    } catch (err) {
      console.error('Error fetching excluded users:', err);
      setExcludedUsers([]);
    }
  };

  const handleAccessToggle = (id, moduleLabel) => {
    if (moduleLabel?.toLowerCase() === 'settings') return;
    setModuleAccess(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddExcludedUser = async () => {
    if (!selectedUserToExclude) return;
    if (excludedUsers.find(u => u.ptsUserID === selectedUserToExclude.ptsUserID)) {
      props.notify('User is already in the excluded list', 'warning');
      return;
    }
    
    props.showSpinner();
    try {
      const newList = [...excludedUsers, selectedUserToExclude];
      const ids = JSON.stringify(newList.map(u => String(u.ptsUserID)));
      
      const existingData = await cdrService.get({ Path: 'Settings.License.ExcludedUsers', CDRKey: 'UserIds' });
      
      if (existingData?.[0]) {
        await cdrService.patch(null, {
          Path: 'Settings.License.ExcludedUsers',
          CDRKey: 'UserIds',
          ValueJson: ids
        });
      } else {
        await cdrService.create({
          Path: 'Settings.License.ExcludedUsers',
          CDRKey: 'UserIds',
          ValueJson: ids
        });
      }
      
      setExcludedUsers(newList);
      setSelectedUserToExclude(null);
      await calculateUsedLicenses(modules);
      props.notify('User added to excluded list', 'success');
    } catch (err) {
      props.handleError(err, 'Error adding excluded user');
    } finally {
      props.hideSpinner();
    }
  };

  const handleRemoveExcludedUser = async (user) => {
    props.showSpinner();
    try {
      const newList = excludedUsers.filter(u => u.ptsUserID !== user.ptsUserID);
      const ids = JSON.stringify(newList.map(u => String(u.ptsUserID)));
      
      await cdrService.patch(null, {
        Path: 'Settings.License.ExcludedUsers',
        CDRKey: 'UserIds',
        ValueJson: ids
      });
      
      setExcludedUsers(newList);
      await calculateUsedLicenses(modules);
      props.notify('User removed from excluded list', 'success');
    } catch (err) {
      props.handleError(err, 'Error removing excluded user');
    } finally {
      props.hideSpinner();
    }
  };

  const handleSaveClick = async () => {
    props.showSpinner();
    try {
      for (const module of modules) {
        const licensePath = `Settings.License.${module.Label}`;
        const accessPath = `Settings.Application.${module.Label}`;
        const id = module.ptsComponentID;

        const licensePayload = {
          Path: licensePath,
          CDRKey: 'LicenseLimit',
          ValueNumeric: Number(maxUsers[id] || 0),
        };

        if (licenseEditMode[id]) {
          await cdrService.patch(null, licensePayload);
        } else {
          await cdrService.create(licensePayload);
          setLicenseEditMode(prev => ({ ...prev, [id]: true }));
        }

        const accessPayload = {
          Path: accessPath,
          CDRKey: 'AccessControl',
          ValueBoolean: moduleAccess[id] === true,
        };

        if (accessEditMode[id]) {
          await cdrService.patch(null, accessPayload);
        } else {
          await cdrService.create(accessPayload);
          setAccessEditMode(prev => ({ ...prev, [id]: true }));
        }
      }
  
      props.notify('License settings saved successfully.', 'Success');
    } catch (err) {
      props.handleError(err, 'Error Saving License Settings.');
    } finally {
      props.hideSpinner();
    }
  };

  const getProgressColor = percentage => {
    if (percentage >= 100) return '#d32f2f';
    if (percentage >= 80) return '#f57c00';
    return '#2e7d32';
  };

  const handleViewUsers = (module) => {
    setSelectedModule(module);
    setShowUsersDialog(true);
  };

  const handleCloseUsersDialog = () => {
    setShowUsersDialog(false);
    setSelectedModule(null);
    setSearchText('');
  };

  const refreshUsersData = async () => {
    const usersRes = await findSearchUsers();
    const users = usersRes.data || usersRes;
    setUsersData(users);
    await calculateUsedLicenses(modules);
  };

  const handleEditUser = (user) => {
    const selectedUser = usersData.find(u => u.ptsUserID === user.ptsUserID);
    props.showAddSecUser({
      selectedUser: selectedUser,
      fetchUsers: refreshUsersData,
      userList: usersData,
      setIsUpdate: () => {},
    });
  };

  const getAssignedUsersForModule = () => {
    if (!selectedModule || !usersData || !profileGroupsData || !groupComponentsData) return [];

    const componentID = Number(selectedModule.ptsComponentID);

    const groupIDs = groupComponentsData
      .filter(
        gc =>
          Number(gc.ptsComponentID) === componentID &&
          Number(gc.IsDeleted) === 0 &&
          Number(gc.Permissions) === 1
      )
      .map(gc => Number(gc.ptsGroupID));

    if (!groupIDs.length) return [];

    const profileIDs = profileGroupsData
      .filter(pg => Number(pg.isDeleted) === 0 && groupIDs.includes(Number(pg.ptsGroupID)))
      .map(pg => Number(pg.ptsProfileID));

    if (!profileIDs.length) return [];

    const assignedUsersList = usersData
      .filter(
        user =>
          Number(user.IsDeleted) === 0 &&
          Number(user.IsDisabled) === 0 &&
          profileIDs.includes(Number(user.ptsProfileID))
      )
      .map(user => ({
        id: user.ptsUserID,
        ptsUserID: user.ptsUserID,
        Username: user.Username,
        Profile: user.Profile || 'N/A',
      }));

    const uniqueUsers = [];
    const seenUserIds = new Set();
    assignedUsersList.forEach(user => {
      if (!seenUserIds.has(user.ptsUserID)) {
        seenUserIds.add(user.ptsUserID);
        uniqueUsers.push(user);
      }
    });

    let filteredUsers = uniqueUsers.sort((a, b) => a.Username.localeCompare(b.Username));

    if (searchText) {
      const search = searchText.toLowerCase();
      filteredUsers = filteredUsers.filter(user =>
        user.Username.toLowerCase().includes(search) ||
        user.Profile.toLowerCase().includes(search)
      );
    }

    return filteredUsers;
  };

  const assignedUsersColumns = [
    { field: 'Username', headerName: 'Username', flex: 1 },
    { field: 'Profile', headerName: 'Profile', flex: 1 },
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Edit User">
          <IconButton size="small" onClick={() => handleEditUser(params.row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const excludedUsersColumns = [
    { field: 'Username', headerName: 'Username', flex: 1 },
    { field: 'FullName', headerName: 'Full Name', flex: 1 },
    { field: 'Profile', headerName: 'Profile', flex: 1 },
    { field: 'AgencyID', headerName: 'Default Agency', flex: 1 },
    { field: 'ptsPersonID', headerName: 'PersonID', width: 100 },
    { field: 'PasswordEmail', headerName: 'Email', flex: 1 },
    {
      field: 'actions',
      headerName: 'Remove',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Remove from excluded list">
          <IconButton size="small" onClick={() => handleRemoveExcludedUser(params.row)}>
            <DeleteIcon color="error" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const getExcludedUsersRows = () => {
    return excludedUsers.map(user => ({
      id: user.ptsUserID,
      ptsUserID: user.ptsUserID,
      Username: user.Username || 'N/A',
      FullName: user.FullName || 'N/A',
      Profile: user.Profile || 'N/A',
      AgencyID: user.AgencyID || 'N/A',
      ptsPersonID: user.ptsPersonID || 'N/A',
      PasswordEmail: user.PasswordEmail || 'N/A',
    }));
  };

  const getAvailableUsersForExclusion = () => {
    const excludedIds = excludedUsers.map(u => u.ptsUserID);
    return usersData.filter(u => !excludedIds.includes(u.ptsUserID) && !u.IsDisabled && !u.IsDeleted);
  };

  const renderDialogActions = () => (
    <Button color="primary" onClick={handleCloseUsersDialog} startIcon={<CloseIcon />}>
      Close
    </Button>
  );

  const renderRow = module => {
    const id = module.ptsComponentID;
    const max = Number(maxUsers[id] || 0);
    const assigned = assignedUsers[id] || 0;
    const percentage = max === 0 ? (assigned > 0 ? 100 : 0) : (assigned / max) * 100;
    const remaining = max - assigned;
    const isAccessEnabled = moduleAccess[id] !== false; // default true
    const isProtectedModule = module.Label?.toLowerCase() === 'settings';

    return (
      <div
        key={id}
        className={classes.row}
        style={!isAccessEnabled ? { backgroundColor: '#fafafa', opacity: 0.85 } : {}}
      >
        {/* Module */}
        <div style={{ textAlign: 'left', display: 'flex', fontWeight: 500 }}>{module.Label}</div>

        {/* Total License */}
        <div className={classes.centerCell}>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            value={max}
            onChange={e => handleMaxUserChange(id, e.target.value)}
            inputProps={{ min: 0 }}
            style={{ width: '200px' }}
            disabled={!isAccessEnabled}
          />
        </div>

        {/* Access Control toggle */}
        <div className={classes.centerCell} style={{ flexDirection: 'column', gap: '4px' }}>
          <Tooltip
            title={
              module.Label.toLowerCase() !== 'settings'
                ? 'Disabling this will disable access to this application for all users'
                : 'Settings application access cannot be modified'
            }
          >
            <span>
              <Switch
                checked={isAccessEnabled}
                onChange={() => handleAccessToggle(id, module.Label)}
                size="small"
                className={isAccessEnabled ? classes.accessEnabled : classes.accessDisabled}
                disabled={isProtectedModule}
              />
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            className={`${classes.accessLabel} ${
              isAccessEnabled ? classes.accessEnabledText : classes.accessDisabledText
            }`}
          >
            {isAccessEnabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </div>

        {/* Used License Badge */}
        <div className={classes.centerCell}>
          <div
            className={classes.badge}
            style={{
              backgroundColor: !isAccessEnabled
                ? '#f5f5f5'
                : percentage >= 100
                ? '#ffebee'
                : percentage >= 80
                ? '#fff3e0'
                : '#e8f5e9',
              color: !isAccessEnabled
                ? '#9e9e9e'
                : percentage >= 100
                ? '#d32f2f'
                : percentage >= 80
                ? '#f57c00'
                : '#2e7d32',
            }}
          >
            {assigned}
          </div>
        </div>

        {/* Remaining + Progress */}
        <div
          className={classes.centerCell}
          style={{ flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e0e0e0',
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${percentage > 100 ? 100 : percentage}%`,
                height: '100%',
                backgroundColor: !isAccessEnabled ? '#bdbdbd' : getProgressColor(percentage),
                transition: 'width 0.3s ease',
              }}
            />
          </div>

          {/* Usage Text */}
          <Typography
            variant="caption"
            style={{
              marginTop: '6px',
              fontWeight: 600,
              color: !isAccessEnabled ? '#9e9e9e' : remaining < 0 ? '#d32f2f' : '#555',
              textAlign: 'left',
              width: '100%',
            }}>
            {remaining >= 0
              ? `${remaining} left (${assigned}/${max})`
              : `Over Limit (${assigned}/${max})`}
          </Typography>
        </div>

        {/* Action */}
        <div className={classes.centerCell}>
          <Tooltip title="View Assigned Users">
            <IconButton size="small" onClick={() => handleViewUsers(module)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <Content>
      <div className={classes.container}>
        <h4>License Management</h4>
        <div className={classes.saveBtn}>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </div>

        <Paper className={classes.tableWrapper}>

          <div className={classes.headerRow}>
            <div style={{textAlign: 'left'}}>Module</div>
            <div>Total License</div>
            <div>Access Control</div>
            <div>Used License</div>
            <div>Remaining License</div>
            <div>Action</div>
          </div>

          {modules.map(m => renderRow(m))}

        </Paper>

        {/* Excluded Users Section */}
        <Paper className={classes.excludedUsersSection}>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Excluded Users from License Count
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
            Users added to this list will not be counted towards module license limits.
          </Typography>
          
          <div className={classes.addUserRow}>
            <Autocomplete
              options={getAvailableUsersForExclusion()}
              getOptionLabel={(option) => `${option.Username} - ${option.FullName || 'N/A'}`}
              value={selectedUserToExclude}
              onChange={(e, value) => setSelectedUserToExclude(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search User to Exclude"
                  variant="outlined"
                  size="small"
                />
              )}
              style={{ width: 400 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddExcludedUser}
              disabled={!selectedUserToExclude}
            >
              Add
            </Button>
          </div>

          <div className={classes.excludedUsersGrid}>
            <XGrid
              columns={excludedUsersColumns}
              rows={getExcludedUsersRows()}
              disableSelectionOnClick
              disableColumnMenu
              density="compact"
              hideFooter={excludedUsers.length <= 100}
            />
          </div>
        </Paper>
      </div>
      <Dialog
        maxWidth="md"
        fullWidth
        open={showUsersDialog}
        onClose={handleCloseUsersDialog}
        title={`Assigned Users - ${selectedModule?.Label || ''}`}
        actions={renderDialogActions()}
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"          
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16, width: 350 }}
        />
        <div className={classes.dialogGrid}>
          <XGrid
            columns={assignedUsersColumns}
            rows={getAssignedUsersForModule()}
            disableSelectionOnClick
            disableColumnMenu
            density="compact"
            hideFooter={getAssignedUsersForModule().length <= 100}
          />
        </div>
      </Dialog>
    </Content>
  );
}

export default connect(null, { handleError, showSpinner, hideSpinner, notify, showAddSecUser })(LicenseManagement);